import React, { useContext, useEffect, useState, useMemo } from 'react';
import styles from './styles.module.css';
import { Link, useHistory } from 'react-router-dom';
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  MenuItem,
  Skeleton,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import StockDamageService from 'services/StockDamageService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ROUTES, SUBSCRIPTION_TYPE } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import { InventoryContext } from 'Context/InventoryContext';
import NumberField from 'components/library/NumberField';
import BatchSelectionPopup from 'components/popups/BatchSelectionPopup';
import { useTranslation } from 'react-i18next';
import { ShopContext } from 'Context/ShopContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';

function NewStockDamage() {
  const { t: tCommon } = useTranslation('common');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts, stockBatches, getStockBatches } = useContext(InventoryContext);
  const { shop } = useContext(ShopContext);
  const { getWarehouses, warehouses, getWarehouseProducts, warehouseProducts } = useContext(WarehouseContext);

  const [item, setItem] = useState(null);
  const [itemPlu, setItemPlu] = useState('');
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [cost, setCost] = useState('0');
  const [date, setDate] = useState(new Date());
  const [totalAmount, setTotalAmount] = useState('0');
  const [damagedItems, setDamagedItems] = useState([]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [selectedItemBatches, setSelectedItemBatches] = useState([]);
  const [batchPopupAnchor, setBatchPopupAnchor] = useState(null);
  const [warehouse, setWarehouse] = useState(null);

  const history = useHistory();

  useEffect(() => {
    setTotalAmount(Number(cost) * Number(quantity));
  }, [cost, quantity]);
  const resetItemFields = () => {
    setItem(null);
    setItemName('');
    setItemPlu('');
    setQuantity('1');
    setCost('0');
    setTotalAmount('0');
    setSelectedItemBatches([]);
    setBatchPopupAnchor(null);
    setItem(null);
  };
  const addItem = () => {
    setDamagedItems([
      ...damagedItems,
      {
        item: item ? item.id : null,
        plu: Number(itemPlu),
        item_name: itemName,
        quantity: Number(quantity),
        rate: cost,
        batch_data: selectedItemBatches,
      },
    ]);
    resetItemFields();
  };
  const handleAddDamage = async () => {
    setLoadingIndicator(true);
    try {
      const data = {
        date: DateTimeHelpers.convertDateToDMY(date),
        damages: damagedItems,
        warehouse_id: warehouse || null,
      };
      await StockDamageService.createStockDamage(data);

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabled = damagedItems.some((item) => item.batch_data && item.batch_data.length > 0);
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);
      postAlertMessage({ text: tCommon('Damage added successfully'), type: 'success' });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }

    history.push(ROUTES.DAMAGE_HISTORY);
    setLoadingIndicator(false);
  };
  const dltItem = (item) => {
    setDamagedItems(damagedItems.filter((itm) => item.item !== itm.item));
  };

  useEffect(() => {
    getProducts();
    getStockBatches();
    if (!featureIsExpired(shop?.warehouse_validity)) {
      getWarehouseProducts({ useCache: true });
    }
  }, [getProducts, getStockBatches, shop?.warehouse_validity, getWarehouseProducts]);

    useEffect(() => {
    const fetchWarehouses = async () => {
      if (!featureIsExpired(shop?.warehouse_validity)) {
        const whs = await getWarehouses({ useCache: true });
        if (whs && whs.length > 0) {
          const defaultWhId = shop?.config?.default_warehouse;
          const defaultWh = whs.find((w) => w.id === defaultWhId);
          setWarehouse(defaultWh ? defaultWh.id : whs[0].id);
        }
      }
    };
    fetchWarehouses();
  }, [shop, getWarehouses]);

  const filteredProductsList = useMemo(() => {
    return products || [];
  }, [products]);

  const currentWarehouseStock = useMemo(() => {
    if (!warehouse || !item || typeof item !== 'object' || !Array.isArray(warehouseProducts)) return Infinity;
    const wp = warehouseProducts.find(
      (p) => String(p.warehouse?.id) === String(warehouse) && String(p.item?.id || p.item) === String(item.id)
    );
    return wp ? wp.stock : 0;
  }, [warehouse, item, warehouseProducts]);

  const filteredStockBatches = useMemo(() => {
    if (!warehouse || !Array.isArray(stockBatches)) return stockBatches || [];
    return stockBatches.filter((b) => String(b.warehouse) === String(warehouse));
  }, [warehouse, stockBatches]);


  const handleBatchQuantityChange = (batchId, val) => {
    let qty = Number(val);
    if (qty < 0) qty = 0;
    setSelectedItemBatches((prev) => {
      const existing = prev.find((b) => b.batch_id === batchId);
      let newBatches;
      if (existing) {
        newBatches = checkboxHandler(prev, batchId, qty);
      } else {
        newBatches = [...prev, { batch_id: batchId, quantity: qty }];
      }
      const totalBatchQty = newBatches.reduce((sum, b) => sum + b.quantity, 0);
      setQuantity(String(totalBatchQty));
      return newBatches;
    });
  };

  const checkboxHandler = (prev, batchId, qty) => {
    if (qty <= 0) {
      return prev.filter((b) => b.batch_id !== batchId);
    }
    return prev.map((b) => (b.batch_id === batchId ? { ...b, quantity: qty } : b));
  };


  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Stock Damages')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 10px' }}>
          <h2 className={styles.subTitle} style={{ margin: 0 }}>
            {tCommon('New damage')}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h3 className={styles.inputLabel} style={{ margin: 0, paddingTop: '15px' }}>
              {tCommon('Damaged Date')}*
            </h3>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={date}
                onChange={(date) => {
                  setDate(date);
                }}
                format="dd/MM/yyyy"
                disableFuture
                slotProps={{
                  textField: {
                    id: 'date-damage',
                    variant: 'standard',
                    style: { minWidth: '150px' },
                  },
                  openPickerButton: {
                    'aria-label': 'change date',
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          {!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled && (
            <Grid item container xs={12} sm={6} md={4} alignItems="center" spacing={1} style={{ marginRight: '20px' }}>
              <Grid item xs={4}>
                <h3 className={styles.inputLabel} style={{ textAlign: 'right' }}>
                  {tCommon('Warehouse')}*
                </h3>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={warehouse || ''}
                  onChange={(e) => {
                    const newWarehouse = e.target.value === '' ? null : Number(e.target.value);
                    if (newWarehouse !== warehouse) {
                      setWarehouse(newWarehouse);
                      resetItemFields();
                      setDamagedItems([]);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{tCommon('None')}</em>
                  </MenuItem>
                  {warehouses.map((wh) => (
                    <MenuItem key={wh.id} value={wh.id}>
                      {wh.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}
        </div>
      </div>

      <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0 0 16px 0', overflowX: 'auto' }}>
        <Table>
          <TableRow key="input row">
            <TableCell>
              <Autocomplete
                value={item || null}
                onChange={(event, newItem) => {
                  if (newItem) {
                    setItem(newItem);
                    setItemPlu(String(newItem.plu));
                    setItemName(newItem.name);
                    setCost(String(newItem.cost));
                    setQuantity(newItem.is_batch_enabled ? '0' : '1');
                    setSelectedItemBatches([]);
                  } else {
                    resetItemFields();
                  }
                }}
                isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                disableClearable
                inputValue={itemPlu}
                onInputChange={(e, value) => {
                  setItemPlu(value);
                }}
                getOptionLabel={(option) => (option && option.plu ? String(option.plu) : '')}
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      String(option.plu).includes(inputValue) ||
                      (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                  )
                }
                options={filteredProductsList}
                renderInput={(params) => (
                  <TextField {...params} label={tCommon('PLU')} variant="outlined" size="small" fullWidth />
                )}
              />
            </TableCell>
            <TableCell>
              <Autocomplete
                value={item || null}
                onChange={(event, newItem) => {
                  if (newItem) {
                    setItem(newItem);
                    setItemPlu(String(newItem.plu));
                    setItemName(newItem.name);
                    setCost(String(newItem.cost));
                    setQuantity(newItem.is_batch_enabled ? '0' : '1');
                    setSelectedItemBatches([]);
                  } else {
                    resetItemFields();
                  }
                }}
                isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                disableClearable
                inputValue={itemName}
                onInputChange={(e, value) => {
                  setItemName(value);
                }}
                getOptionLabel={(option) => (option && option.name ? option.name : '')}
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                      (option.ean && option.ean.toLowerCase().includes(inputValue.toLowerCase()))
                  )
                }
                options={filteredProductsList}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tCommon('Search by Name/ EAN')}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                style={{ minWidth: '240px' }}
              />
            </TableCell>
            <TableCell>
              <NumberField label={tCommon('Cost')} value={cost} disabled style={{ minWidth: '75px' }} />
            </TableCell>
            <TableCell>
              <div style={{ position: 'relative' }}>
                {item?.is_batch_enabled ? (
                  <NumberField
                    type="text"
                    label={tCommon('Quantity')}
                    value={quantity}
                    style={{ minWidth: '100px', cursor: 'pointer' }}
                    sx={{ '& input': { cursor: 'pointer' } }}
                    inputProps={{ readOnly: true }}
                    onKeyDown={(e) => e.preventDefault()}
                    InputProps={{
                      onClick: (e) => {
                        setBatchPopupAnchor(e.currentTarget);
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton color="primary" size="small">
                            <ArrowDropDownIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <NumberField
                    label={tCommon('Quantity')}
                    value={quantity}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (val < 0) val = 0;
                      setQuantity(String(val));
                    }}
                    style={{ minWidth: '75px' }}
                  />
                )}
                {warehouse && item && (
                  <div
                    style={{
                      fontSize: '10px',
                      color: '#666',
                      position: 'absolute',
                      bottom: '-16px',
                      left: '4px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tCommon('Stock')}: {currentWarehouseStock}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              <NumberField label={tCommon('Total Amount')} value={totalAmount} disabled style={{ minWidth: '75px' }} />
            </TableCell>
            <TableCell>
              <IconButton
                variant="contained"
                disabled={itemPlu === '' || itemName === '' || cost === '' || Number(quantity) <= 0}
                onClick={addItem}
              >
                <AddIcon />
              </IconButton>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </Table>
      </div>
      <TableContainer style={{ marginTop: 4 }} component={Paper}>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              <TableCell className={styles.columnCell}>{tCommon('PLU')}</TableCell>
              <TableCell className={styles.nameColumn}>{tCommon('Item Name')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Cost')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Quantity')}</TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Total')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(loadingIndicator || products === null) &&
              [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" animation="wave" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
            {damagedItems &&
              damagedItems.map((item) => (
                <TableRow key={item.plu}>
                  <TableCell>{item.plu}</TableCell>
                  <TableCell>
                    {item.item_name}
                    {item.batch_data && item.batch_data.length > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {item.batch_data.map((sb, idx) => {
                          const batch = (stockBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                          return (
                            <span key={sb.batch_id}>
                              {batch?.batch_name || batch?.batch_no || sb.batch_id}: {sb.quantity}
                              {idx < item.batch_data.length - 1 ? ', ' : ''}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{item.rate}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{Number(item.rate * item.quantity)}</TableCell>
                  <TableCell>
                    <Button variant="contained" onClick={() => dltItem(item)}>
                      <DeleteIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container className={styles.submitSection} justify="flex-end">
        <Link to={ROUTES.DAMAGE_HISTORY} className={styles.link}>
          <Button variant="contained" color="secondary" className={styles.closeBtn}>
            {tCommon('Cancel')}
          </Button>
        </Link>
        <Button
          variant="contained"
          color="primary"
          style={{ backgroundColor: '#00a65a' }}
          disabled={date === '' || damagedItems.length === 0 || loadingIndicator}
          onClick={handleAddDamage}
        >
          {tCommon('Save Damage')}
        </Button>
      </Grid>
      <BatchSelectionPopup
        open={Boolean(batchPopupAnchor)}
        anchorEl={batchPopupAnchor}
        onClose={() => setBatchPopupAnchor(null)}
        item={item}
        stockBatches={filteredStockBatches}
        selectedBatches={selectedItemBatches}
        onChange={handleBatchQuantityChange}
        showExpired={true}
      />
    </div>
  );
}

export default withConsoleBase(NewStockDamage);
